import routes from 'Routes';
import { useBlacklistJwtToken } from 'ServiceHooks/authHooks';
import { useLayoutStore } from 'Store/LayoutStore';
import { DEFAULT_ERROR_MESSAGE } from 'Utilities/constants';
import { message } from 'antd';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

const AutoLogout = ({ children }) => {
  const navigate = useNavigate();
  const [userSettings] = useLocalStorage('user');
  const [messageApi] = message.useMessage();
  const autoTime = userSettings?.auto_logout_time ? userSettings?.auto_logout_time : 15
  const logoutTime = autoTime * 60 * 1000;
  const logoutTimer = useRef(null);
  const frontendURL = window.location.origin;
  const { toggleNotification, clearReadNotifications, resetGridColumPreference } = useLayoutStore((state) => ({
    toggleNotification: state.toggleNotification,
    clearReadNotifications: state.clearReadNotifications,
    resetGridColumPreference: state.resetGridColumPreference,
  }));

  const { mutateAsync: logout } = useBlacklistJwtToken({
    onError: async (error) => {
      const isLoggedOut = error?.error_info?.detail?.includes('is blacklisted');
      const type = isLoggedOut ? 'info' : 'error';
      const content = isLoggedOut ? 'Already logged out.' : DEFAULT_ERROR_MESSAGE;
      await messageApi.open({ type, content });
    },
  });

  // Handle user logout
  const handleLogout = useCallback(async () => {
    toggleNotification(false);
    clearReadNotifications();
    await logout();
    localStorage.removeItem('user');
    localStorage.removeItem('user_id');
    localStorage.removeItem('userPreference');
    localStorage.removeItem('company_id');
    localStorage.removeItem('yvi-persisted');
    resetGridColumPreference();
    if(frontendURL === "https://app.yvi.ai"){
      navigate('/OTPForm');
    }else {
      navigate(routes.LOGIN);
    }
  }, [navigate, toggleNotification, clearReadNotifications, logout, resetGridColumPreference]);

  // Start or reset the logout timer
  const resetTimer = useCallback(() => {
    clearTimeout(logoutTimer.current);
    logoutTimer.current = setTimeout(handleLogout, logoutTime);
  }, [handleLogout]);

  useEffect(() => {
    const handleActivity = () => {
      if (document.hasFocus()) {
        resetTimer();
      }
    };

    // List of events to track user activity
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

    // Start the timer initially
    resetTimer();

    // Add event listeners for user activity
    events.forEach(event => window.addEventListener(event, handleActivity));

    // Handle focus and blur of the window
    window.addEventListener('focus', resetTimer);  // Reset timer on focus
    window.addEventListener('blur', resetTimer);   // Start timer on blur

    return () => {
      clearTimeout(logoutTimer.current);
      events.forEach(event => window.removeEventListener(event, handleActivity));
      window.removeEventListener('focus', resetTimer);
      window.removeEventListener('blur', resetTimer);
    };
  }, [resetTimer]);

  return children;
};

export default AutoLogout;
